import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X, Sparkles, ArrowRight } from 'lucide-react';

/**
 * FindOptionBanner — slim site-wide announcement bar directing visitors to
 * the "Find My Best Training Option" questionnaire on /find-my-class.
 *
 * ✱ Visibility (all must be true):
 *   • Route is NOT in EXCLUDED_PREFIXES (auth / checkout / policy / admin /
 *     coach portals).
 *   • The banner has not been dismissed earlier in THIS browser session
 *     (sessionStorage → `SESSION_DISMISSED_KEY`).
 *
 * ✱ NOT gated by:
 *   • Auth state — the banner is shown to guests AND logged-in customers
 *     because it links to a public tool.
 *   • Any localStorage flag — dismissal is deliberately session-scoped so
 *     the banner is eligible to reappear in the next browser session.
 *
 * ✱ Interactions (both dismiss for the session only, never permanently):
 *   • CTA click → mark session-dismissed + navigate to /find-my-class.
 *   • × close  → mark session-dismissed.
 *
 * ✱ Tracking beacon: anonymous events fired to /api/analytics/banner-event
 *   for shown / clicked / closed.
 */

const SESSION_DISMISSED_KEY = 'lgra_find_option_banner_session_dismissed';
const BANNER_KEY = 'find_best_option';
const CTA_ROUTE = '/find-my-class';

// Prefixes where the banner should NOT show. Includes admin + coach portals
// per the Feb 25 2026 spec — the intro is only for public-facing pages.
const EXCLUDED_PREFIXES = [
  '/find-my-class',
  '/login', '/register', '/reset-password', '/forgot-password',
  '/checkout', '/payment', '/booking-confirmation',
  '/admin', '/coach', '/coach-portal',
  '/terms', '/privacy', '/policies',
  '/booking-help',
];

const isExcluded = (pathname) => {
  const p = (pathname || '').split('?')[0];
  return EXCLUDED_PREFIXES.some((prefix) => p === prefix || p.startsWith(prefix + '/') || p.startsWith(prefix + '?'));
};

const readFlag = (storage, key) => {
  try { return storage.getItem(key) === 'true'; } catch { return false; }
};
const writeFlag = (storage, key) => {
  try { storage.setItem(key, 'true'); } catch { /* ignore */ }
};

// Stable visitor hash for analytics de-dup; session-scoped is fine.
const visitorHash = () => {
  try {
    let h = window.localStorage.getItem('lgra_visitor_hash');
    if (!h) {
      h = Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
      window.localStorage.setItem('lgra_visitor_hash', h);
    }
    return h;
  } catch { return ''; }
};

const track = (event, path) => {
  try {
    const API = process.env.REACT_APP_BACKEND_URL;
    if (!API) return;
    fetch(`${API}/api/analytics/banner-event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        banner: BANNER_KEY,
        event,
        path,
        visitor_hash: visitorHash(),
      }),
    }).catch(() => {});
  } catch { /* ignore */ }
};

export const FindOptionBanner = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  // Fire the `shown` beacon only once per mounted lifecycle to avoid double
  // counting when the same banner survives multiple route changes.
  const [beaconFiredForPath, setBeaconFiredForPath] = useState(null);

  const excluded = useMemo(() => isExcluded(location.pathname), [location.pathname]);

  // Re-evaluate on every route change. The banner survives navigation between
  // public pages so returning visitors keep seeing it until they dismiss it.
  useEffect(() => {
    if (excluded) { setVisible(false); return; }
    if (readFlag(window.sessionStorage, SESSION_DISMISSED_KEY)) {
      setVisible(false);
      return;
    }
    setVisible(true);
  }, [excluded, location.pathname]);

  // Fire `shown` beacon once per (session · path). Cheap and non-blocking.
  useEffect(() => {
    if (!visible) return;
    if (beaconFiredForPath === location.pathname) return;
    track('shown', location.pathname);
    setBeaconFiredForPath(location.pathname);
  }, [visible, location.pathname, beaconFiredForPath]);

  const handleClick = useCallback(() => {
    track('clicked', location.pathname);
    writeFlag(window.sessionStorage, SESSION_DISMISSED_KEY);
    setVisible(false);
    navigate(CTA_ROUTE);
  }, [location.pathname, navigate]);

  const handleClose = useCallback((e) => {
    e.stopPropagation();
    track('closed', location.pathname);
    writeFlag(window.sessionStorage, SESSION_DISMISSED_KEY);
    setVisible(false);
  }, [location.pathname]);

  if (!visible) return null;

  return (
    <div
      role="region"
      aria-label="Find My Best Training Option announcement"
      className="relative z-30 border-b border-[#E9E3FF] bg-gradient-to-r from-[#F6F1FF] via-[#EFE7FF] to-[#F6F1FF]"
      data-testid="find-option-banner"
    >
      <button
        type="button"
        onClick={handleClick}
        className="w-full text-left flex items-center justify-center gap-2 py-2 px-10 sm:px-16 text-[13px] sm:text-sm font-semibold text-[#3F2774] hover:bg-white/40 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#6D45D8] focus-visible:ring-offset-1"
        data-testid="find-option-banner-cta"
        aria-label="Open Find My Best Training Option"
      >
        <Sparkles size={14} className="text-[#6D45D8] shrink-0" aria-hidden />
        <span className="truncate">
          <span className="hidden sm:inline">Not sure where to begin? </span>
          <span className="sm:hidden">New here? </span>
          <span className="font-black tracking-tight text-[#3F2774] underline decoration-[#6D45D8]/60 decoration-2 underline-offset-2">
            Find My Best Training Option
          </span>
        </span>
        <ArrowRight size={14} className="text-[#6D45D8] shrink-0" aria-hidden />
      </button>
      <button
        type="button"
        onClick={handleClose}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-[#6D45D8] hover:bg-white/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#6D45D8]"
        aria-label="Close announcement"
        data-testid="find-option-banner-close"
      >
        <X size={16} />
      </button>
    </div>
  );
};

export default FindOptionBanner;
